import type { Editor } from '@tiptap/react'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'

export type PolicyClaimBlockRef = {
  id: string
  docBlockId?: string | null
}

export type PolicyClaimDecorationRef = PolicyClaimBlockRef & {
  claimText?: string | null
  quoteText?: string | null
  templateName?: string | null
  templateKey?: string | null
  verification?: string | null
  confidence?: string | null
  triageStatus?: string | null
  draftClaimId?: string | null
  /** True when the claim has at least one linked test/CES — drives green highlight. */
  hasLinkedTests?: boolean
  /** True when the claim is awaiting human triage — drives amber Review highlight. */
  needsReview?: boolean
  /** RFC-140 §6.3: Compiler model match score (0.0–1.0) */
  slotMatchScore?: number | null
  /** RFC-140 §6.3: Match level — "slot", "check", or "control" */
  matchLevel?: string | null
}

type ClaimVisualState = 'default' | 'flagged'

export function buildPolicyClaimBlockMaps(claims: PolicyClaimBlockRef[]) {
  const claimIdToBlockId: Record<string, string> = {}
  const blockIdToClaimIds: Record<string, string[]> = {}

  for (const claim of claims) {
    const blockId = claim.docBlockId?.trim()
    if (!blockId) continue

    claimIdToBlockId[claim.id] = blockId
    if (!blockIdToClaimIds[blockId]) {
      blockIdToClaimIds[blockId] = []
    }
    blockIdToClaimIds[blockId].push(claim.id)
  }

  return { claimIdToBlockId, blockIdToClaimIds }
}

function getNodeBlockId(node: { attrs?: Record<string, string | null | undefined> }) {
  return node.attrs?.id ?? node.attrs?.['data-dg-id'] ?? null
}

function escapeSelector(value: string) {
  if (typeof window !== 'undefined' && window.CSS?.escape) {
    return window.CSS.escape(value)
  }

  return value.replace(/["\\]/g, '\\$&')
}

function findBlockElement(container: HTMLElement, blockId: string) {
  const escaped = escapeSelector(blockId)
  return container.querySelector<HTMLElement>(`[id="${escaped}"], [data-id="${escaped}"], [data-dg-id="${escaped}"]`)
}

function isVerticallyScrollable(element: HTMLElement) {
  const { overflowY } = window.getComputedStyle(element)
  return (
    ['auto', 'scroll', 'overlay'].includes(overflowY) &&
    element.scrollHeight > element.clientHeight
  )
}

function findScrollableAncestorWithin(
  container: HTMLElement,
  element: HTMLElement
) {
  let current: HTMLElement | null = element.parentElement

  while (current) {
    if (isVerticallyScrollable(current)) {
      return current
    }

    if (current === container) {
      break
    }

    current = current.parentElement
  }

  return isVerticallyScrollable(container) ? container : null
}

const CLAIM_BADGE_COLORS = {
  default: {
    active: {
      bg: 'var(--policy-claim-neutral-active-bg)',
      border: 'var(--policy-claim-neutral-active-border)',
      text: 'var(--policy-claim-neutral-active-text)',
    },
    inactive: {
      bg: 'var(--policy-claim-neutral-inactive-bg)',
      border: 'var(--policy-claim-neutral-inactive-border)',
      text: 'var(--policy-claim-neutral-inactive-text)',
    },
    block: {
      active: 'var(--policy-claim-neutral-block-active-bg)',
      inactive: 'var(--policy-claim-neutral-block-inactive-bg)',
    },
    blockShadow: {
      active: 'var(--policy-claim-neutral-block-active-shadow)',
      inactive: 'var(--policy-claim-neutral-block-inactive-shadow)',
    },
  },
  flagged: {
    active: {
      bg: 'var(--policy-claim-flagged-active-bg)',
      border: 'var(--policy-claim-flagged-active-border)',
      text: 'var(--status-orange-strong)',
    },
    inactive: {
      bg: 'var(--policy-claim-flagged-inactive-bg)',
      border: 'var(--policy-claim-flagged-inactive-border)',
      text: 'var(--status-orange-strong)',
    },
    block: {
      active: 'var(--policy-claim-flagged-block-active-bg)',
      inactive: 'var(--policy-claim-flagged-block-inactive-bg)',
    },
    blockShadow: {
      active: 'var(--policy-claim-flagged-block-active-shadow)',
      inactive: 'var(--policy-claim-flagged-block-inactive-shadow)',
    },
  },
} as const

function getClaimVisualState(triageStatus?: string | null): ClaimVisualState {
  if (triageStatus === 'flagged') return 'flagged'
  return 'default'
}

function getClaimColors(state: ClaimVisualState, active: boolean) {
  const palette = CLAIM_BADGE_COLORS[state]
  return active ? palette.active : palette.inactive
}

function getClaimHighlightVariant(
  claim: {
    triageStatus?: string | null
    hasLinkedTests?: boolean
    needsReview?: boolean
  }
): 'review' | 'dismissed' | 'linked' | 'default' {
  // Review covers both flagged claims and ones still awaiting human triage —
  // they share the amber "Review" pill in the meta bar.
  if (claim.triageStatus === 'flagged' || claim.needsReview) return 'review'
  if (claim.triageStatus === 'dismissed') return 'dismissed'
  if (claim.hasLinkedTests) return 'linked'
  return 'default'
}

// Locate a substring inside a block node and map it to ProseMirror document
// positions, walking text descendants so the range survives nested wrappers
// (list items, paragraphs, marks). Returns null when the needle is missing.
function findTextRangeInBlock(
  node: { textContent: string; descendants: (fn: (child: any, offset: number) => boolean | void) => void },
  parentPos: number,
  needle: string
): { from: number; to: number } | null {
  const trimmed = needle.trim()
  if (!trimmed) return null
  const textParts: string[] = []
  node.descendants((child: any) => {
    if (child.isText) textParts.push(child.text ?? '')
    else if (child.type.name === 'hardBreak') textParts.push('\n')
  })
  const idx = textParts.join('').indexOf(trimmed)
  if (idx === -1) return null
  const targetStart = idx
  const targetEnd = idx + trimmed.length

  let acc = 0
  let from: number | null = null
  let to: number | null = null

  node.descendants((child: any, offset: number) => {
    if (from !== null && to !== null) return false
    if (!child.isText && child.type.name !== 'hardBreak') return true
    const text = child.isText ? (child.text as string | undefined) ?? '' : '\n'
    const start = acc
    const end = acc + text.length

    if (from === null && targetStart >= start && targetStart <= end) {
      from = parentPos + 1 + offset + (targetStart - start)
    }
    if (to === null && targetEnd > start && targetEnd <= end) {
      to = parentPos + 1 + offset + (targetEnd - start)
    }

    acc = end
    return false
  })

  return from !== null && to !== null ? { from, to } : null
}

export function registerPolicyClaimBadgeDecorations(params: {
  editor: Editor
  pluginKey: PluginKey
  claims: PolicyClaimDecorationRef[]
  activeClaimId?: string | null
  onClaimClick?: (claimId: string) => void
  onClaimFix?: (claim: PolicyClaimDecorationRef) => void
  onClaimEdit?: (claim: PolicyClaimDecorationRef) => void
  onClaimDelete?: (claim: PolicyClaimDecorationRef) => void
  editable?: boolean
  showHighlights?: boolean
  /** Render inline claim IDs and flagged claim cards in the document. */
  showClaimMarkers?: boolean
}) {
  const {
    editor,
    pluginKey,
    claims,
    activeClaimId,
    onClaimClick,
    onClaimFix,
    onClaimEdit,
    onClaimDelete,
    editable = false,
    showHighlights = false,
    showClaimMarkers = true,
  } = params

  if (!editor || editor.isDestroyed) return undefined

  try {
    const hasPlugin = editor.state.plugins.some((plugin) => plugin.spec.key === pluginKey)
    if (hasPlugin) {
      editor.unregisterPlugin(pluginKey)
    }
  } catch {
    // Ignore cleanup errors for editors that are still mounting.
  }

  if (claims.length === 0) {
    if (typeof window !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn('[claim-pills] no claims passed to decorator')
    }
    return undefined
  }

  const { blockIdToClaimIds } = buildPolicyClaimBlockMaps(claims)
  const claimsById = new Map(claims.map((claim) => [claim.id, claim]))

  if (Object.keys(blockIdToClaimIds).length === 0) {
    if (typeof window !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn('[claim-pills] all claims missing docBlockId', {
        total: claims.length,
        sample: claims.slice(0, 3).map((c) => ({
          id: c.id,
          docBlockId: c.docBlockId,
        })),
      })
    }
    return undefined
  }

  if (typeof window !== 'undefined') {
    const blockIds = new Set<string>()
    editor.state.doc.descendants((node) => {
      const id = getNodeBlockId(node)
      if (id) blockIds.add(id)
    })
    const claimBlockIds = Object.keys(blockIdToClaimIds)
    const missing = claimBlockIds.filter((id) => !blockIds.has(id))
    // eslint-disable-next-line no-console
    console.log('[claim-pills] registering decorations', {
      claimCount: claims.length,
      docBlockIds: blockIds.size,
      claimBlockIds: claimBlockIds.length,
      missingInDoc: missing.length,
      sampleMissing: missing.slice(0, 3),
      sampleDocIds: Array.from(blockIds).slice(0, 3),
    })
  }

  const plugin = new Plugin({
    key: pluginKey,
    props: {
      decorations(state) {
        const decorations: Decoration[] = []

        const buildClaimActions = (
          claim: PolicyClaimDecorationRef,
          options: { isFlagged: boolean; anchor: HTMLElement }
        ) => {
          const { isFlagged, anchor } = options
          const actionRow = document.createElement(isFlagged ? 'div' : 'span')
          actionRow.style.cssText = isFlagged
            ? 'display:flex;align-items:center;gap:6px;flex-shrink:0;padding-top:2px;'
            : 'display:inline-flex;align-items:center;gap:4px;flex-shrink:0;'

          const makeActionButton = (label: string, style: string, onClick?: () => void) => {
            if (!onClick) return
            const button = document.createElement('button')
            button.type = 'button'
            button.textContent = label
            button.style.cssText = style
            button.onfocus = () => {
              button.style.boxShadow = 'var(--policy-claim-marker-focus-shadow)'
            }
            button.onblur = () => {
              button.style.boxShadow = 'none'
            }
            button.onclick = (event) => {
              event.preventDefault()
              event.stopPropagation()
              onClick()
            }
            actionRow.appendChild(button)
          }

          if (isFlagged && onClaimFix) {
            makeActionButton(
              'Fix',
              'font-size:10px;padding:4px 8px;border-radius:4px;background-color:var(--policy-claim-fix-bg);color:var(--policy-claim-fix-text);border:none;cursor:pointer;font-family:Inter,sans-serif;font-weight:600;',
              () => onClaimFix(claim)
            )
          }

          if (editable && (onClaimEdit || onClaimDelete)) {
            const menuWrap = document.createElement('span')
            menuWrap.style.cssText = 'position:relative;display:inline-flex;align-items:center;'

            const menuTrigger = document.createElement('button')
            menuTrigger.type = 'button'
            menuTrigger.setAttribute('aria-label', 'Claim actions')
            menuTrigger.setAttribute('aria-haspopup', 'menu')
            menuTrigger.setAttribute('aria-expanded', 'false')
            menuTrigger.title = 'Claim actions'
            menuTrigger.style.cssText = [
              'display:flex',
              'align-items:center',
              'justify-content:center',
              `width:${isFlagged ? '30px' : 'auto'}`,
              `height:${isFlagged ? '30px' : 'auto'}`,
              `padding:${isFlagged ? '0' : '1px 4px'}`,
              'border-radius:4px',
              `background-color:${isFlagged ? 'var(--policy-claim-action-bg)' : 'var(--policy-claim-marker-bg)'}`,
              `border:1px solid ${isFlagged ? 'var(--policy-claim-action-border)' : 'var(--policy-claim-marker-border)'}`,
              `color:${isFlagged ? 'var(--policy-claim-action-text)' : 'var(--policy-claim-marker-text)'}`,
              'cursor:pointer',
              `font-size:${isFlagged ? '0' : '11px'}`,
              `line-height:${isFlagged ? '0' : '1'}`,
              `font-family:${isFlagged ? 'inherit' : 'Inter,sans-serif'}`,
              `font-weight:${isFlagged ? 'inherit' : '600'}`,
              `transform:${isFlagged ? 'none' : 'translateY(-0.45em)'}`,
              `opacity:${isFlagged ? '0' : '1'}`,
              `pointer-events:${isFlagged ? 'none' : 'auto'}`,
              'transition:opacity 0.15s ease, background-color 0.15s ease, border-color 0.15s ease',
            ].join('; ')
            if (isFlagged) {
              menuTrigger.innerHTML =
                '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 20h4l10-10-4-4L4 16v4Z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path><path d="m13 7 4 4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path></svg>'
            }
            if (!isFlagged) {
              menuTrigger.onmouseenter = () => {
                menuTrigger.style.backgroundColor = 'var(--policy-claim-marker-hover-bg)'
                menuTrigger.style.borderColor = 'var(--policy-claim-marker-hover-border)'
                menuTrigger.style.color = 'var(--policy-claim-marker-hover-text)'
              }
              menuTrigger.onmouseleave = () => {
                menuTrigger.style.backgroundColor = 'var(--policy-claim-marker-bg)'
                menuTrigger.style.borderColor = 'var(--policy-claim-marker-border)'
                menuTrigger.style.color = 'var(--policy-claim-marker-text)'
              }
            }

            const menu = document.createElement('div')
            menu.style.cssText = [
              'position:absolute',
              'top:calc(100% + 6px)',
              'right:0',
              'min-width:140px',
              'padding:6px',
              'border-radius:10px',
              'border:1px solid var(--policy-claim-menu-border)',
              'background-color:var(--popover)',
              'box-shadow:var(--policy-claim-menu-shadow)',
              'display:none',
              'z-index:5',
            ].join('; ')
            menu.setAttribute('role', 'menu')

            const makeMenuItem = (label: string, color: string, onClick?: () => void) => {
              if (!onClick) return
              const item = document.createElement('button')
              item.type = 'button'
              item.textContent = label
              item.setAttribute('role', 'menuitem')
              item.style.cssText = [
                'display:flex',
                'align-items:center',
                'width:100%',
                'padding:7px 10px',
                'border:none',
                'border-radius:4px',
                'background:transparent',
                `color:${color}`,
                'font-size:12px',
                'font-family:Inter,sans-serif',
                'cursor:pointer',
                'text-align:left',
              ].join('; ')
              item.onmouseenter = () => {
                item.style.backgroundColor = 'var(--policy-claim-menu-hover-bg)'
              }
              item.onmouseleave = () => {
                item.style.backgroundColor = 'transparent'
              }
              item.onfocus = () => {
                item.style.backgroundColor = 'var(--policy-claim-menu-hover-bg)'
              }
              item.onblur = () => {
                item.style.backgroundColor = 'transparent'
              }
              const handleMenuItemClick = (event: Event) => {
                event.preventDefault()
                event.stopPropagation()
                menu.style.display = 'none'
                menuTrigger.setAttribute('aria-expanded', 'false')
                onClick()
              }
              item.addEventListener('mousedown', handleMenuItemClick)
              item.onclick = handleMenuItemClick as any
              menu.appendChild(item)
            }

            if (onClaimEdit) {
              makeMenuItem('Edit Claim', 'var(--policy-claim-action-text)', () => onClaimEdit(claim))
            }

            if (onClaimDelete) {
              makeMenuItem('Delete Claim', 'var(--policy-claim-menu-delete-text)', () => onClaimDelete(claim))
            }

            let hideMenuTimer: ReturnType<typeof setTimeout> | null = null

            const cancelScheduledHide = () => {
              if (hideMenuTimer) {
                clearTimeout(hideMenuTimer)
                hideMenuTimer = null
              }
            }

            const hideMenu = () => {
              cancelScheduledHide()
              menu.style.display = 'none'
              menuTrigger.setAttribute('aria-expanded', 'false')
            }

            const scheduleHideMenu = () => {
              cancelScheduledHide()
              hideMenuTimer = setTimeout(() => {
                if (isFlagged) {
                  hideMenuTrigger()
                }
                hideMenu()
              }, 120)
            }

            const showMenuTrigger = () => {
              if (!isFlagged) return
              menuTrigger.style.opacity = '1'
              menuTrigger.style.pointerEvents = 'auto'
            }

            const hideMenuTrigger = () => {
              if (!isFlagged) return
              menuTrigger.style.opacity = '0'
              menuTrigger.style.pointerEvents = 'none'
            }

            anchor.onmouseenter = () => {
              cancelScheduledHide()
              if (isFlagged) {
                showMenuTrigger()
              } else {
                menu.style.display = 'block'
                menuTrigger.setAttribute('aria-expanded', 'true')
              }
            }
            anchor.onmouseleave = () => {
              if (anchor.contains(document.activeElement)) return
              scheduleHideMenu()
            }
            anchor.onfocusin = () => {
              cancelScheduledHide()
              if (isFlagged) {
                showMenuTrigger()
              } else {
                menu.style.display = 'block'
                menuTrigger.setAttribute('aria-expanded', 'true')
              }
            }
            anchor.onfocusout = (event) => {
              const nextTarget = event.relatedTarget
              if (nextTarget instanceof Node && anchor.contains(nextTarget)) {
                return
              }
              scheduleHideMenu()
            }

            menuTrigger.onfocus = () => {
              showMenuTrigger()
              if (!isFlagged) {
                menuTrigger.style.backgroundColor = 'var(--policy-claim-marker-hover-bg)'
                menuTrigger.style.borderColor = 'var(--policy-claim-marker-hover-border)'
                menuTrigger.style.color = 'var(--policy-claim-marker-hover-text)'
              }
              menuTrigger.style.boxShadow = 'var(--policy-claim-marker-focus-shadow)'
            }
            menuTrigger.onblur = () => {
              if (!isFlagged) {
                menuTrigger.style.backgroundColor = 'var(--policy-claim-marker-bg)'
                menuTrigger.style.borderColor = 'var(--policy-claim-marker-border)'
                menuTrigger.style.color = 'var(--policy-claim-marker-text)'
              }
              menuTrigger.style.boxShadow = 'none'
            }

            menuWrap.appendChild(menuTrigger)
            if (!isFlagged) {
              const handleMenuTriggerClick = (event: Event) => {
                event.preventDefault()
                event.stopPropagation()
                onClaimClick?.(claim.id)
                cancelScheduledHide()
                const isCurrentlyOpen = menu.style.display === 'block'
                menu.style.display = isCurrentlyOpen ? 'none' : 'block'
                menuTrigger.setAttribute('aria-expanded', menu.style.display === 'block' ? 'true' : 'false')
              }
              menuTrigger.addEventListener('mousedown', handleMenuTriggerClick)
              menuTrigger.onclick = handleMenuTriggerClick as any
              if (menu.childNodes.length > 0) {
                menuWrap.appendChild(menu)
              }
            } else if (menu.childNodes.length > 0) {
              menuTrigger.onclick = (event) => {
                event.preventDefault()
                event.stopPropagation()
                const isOpen = menu.style.display !== 'none'
                menu.style.display = isOpen ? 'none' : 'block'
                menuTrigger.setAttribute('aria-expanded', isOpen ? 'false' : 'true')
              }
              menuWrap.appendChild(menu)
            }
            menu.onmouseenter = () => {
              cancelScheduledHide()
            }
            menu.onmouseleave = () => {
              scheduleHideMenu()
            }
            actionRow.appendChild(menuWrap)
          }

          return actionRow
        }

        state.doc.descendants((node, pos) => {
          const blockId = getNodeBlockId(node)
          const claimIds = blockId ? blockIdToClaimIds[blockId] : undefined
          if (!blockId || !claimIds) return

          const claimsForBlock = claimIds
            .map((claimId) => claimsById.get(claimId))
            .filter((claim): claim is PolicyClaimDecorationRef => Boolean(claim))

          const inlineClaims = claimsForBlock.filter(
            (claim) => getClaimVisualState(claim.triageStatus) !== 'flagged'
          )
          const flaggedClaims = claimsForBlock.filter(
            (claim) => getClaimVisualState(claim.triageStatus) === 'flagged'
          )

          if (showHighlights && claimsForBlock.length > 0) {
            for (const claim of claimsForBlock) {
              const variant = getClaimHighlightVariant(claim)
              const className = `policy-claim-highlight policy-claim-highlight--${variant}`
              // Prefer the verbatim quote; fall back to claimText only as a last
              // resort because it's typically a paraphrase that won't match.
              const quote = (claim.quoteText ?? '').trim()
              const claimSnippet = (claim.claimText ?? '').trim()
              const range =
                (quote && findTextRangeInBlock(node as any, pos, quote)) ||
                (claimSnippet && findTextRangeInBlock(node as any, pos, claimSnippet)) ||
                null
              if (range) {
                decorations.push(
                  Decoration.inline(range.from, range.to, { class: className })
                )
              }
              // Multiple claims with no resolvable span: skip — the CLM-XXXX
              // pill at the end of the block still anchors the claim.
            }
          }

          if (showClaimMarkers && inlineClaims.length > 0) {
            decorations.push(
              Decoration.widget(
                pos + node.nodeSize - 1,
                () => {
                  const inlineWrapper = document.createElement('span')
                  inlineWrapper.style.cssText =
                    'display:inline-flex;align-items:center;gap:2px;margin-left:6px;white-space:nowrap;'

                  for (const [inlineIndex, claim] of inlineClaims.entries()) {
                    const shortClaimId = 'CLM-' + claim.id.slice(-4).toUpperCase()
                    const markerWrap = document.createElement('span')
                    markerWrap.style.cssText =
                      'display:inline-flex;align-items:center;gap:0;position:relative;'

                    const actionRow = buildClaimActions(claim, {
                      isFlagged: false,
                      anchor: markerWrap,
                    })
                    const markerButton = actionRow.querySelector('button')
                    if (markerButton instanceof HTMLButtonElement) {
                      markerButton.textContent = shortClaimId
                      markerButton.title = shortClaimId
                      markerButton.setAttribute('aria-label', shortClaimId)
                    }
                    if (actionRow.childNodes.length > 0) {
                      markerWrap.appendChild(actionRow)
                    } else {
                      // No action buttons — render a claim ID citation marker with dropdown
                      const shortId = 'CLM-' + claim.id.slice(-4).toUpperCase()
                      const plainMarker = document.createElement('button')
                      plainMarker.type = 'button'
                      plainMarker.textContent = shortId
                      plainMarker.setAttribute('data-claim-id', claim.id)
                      plainMarker.style.cssText =
                        'display:inline-flex;align-items:center;justify-content:center;padding:1px 6px;border-radius:4px;background-color:var(--policy-claim-marker-bg);border:1px solid var(--policy-claim-marker-border);color:var(--policy-claim-marker-text);cursor:pointer;font-size:10px;line-height:1;font-family:Inter,sans-serif;font-weight:600;transform:translateY(-0.45em);transition:background-color 0.15s ease,border-color 0.15s ease;'
                      plainMarker.title = shortId
                      plainMarker.onmouseenter = () => {
                        plainMarker.style.backgroundColor = 'var(--policy-claim-marker-hover-bg)'
                        plainMarker.style.borderColor = 'var(--policy-claim-marker-hover-border)'
                      }
                      plainMarker.onmouseleave = () => {
                        if (menu.style.display === 'block') return
                        plainMarker.style.backgroundColor = 'var(--policy-claim-marker-bg)'
                        plainMarker.style.borderColor = 'var(--policy-claim-marker-border)'
                      }

                      const menu = document.createElement('div')
                      menu.style.cssText = 'position:absolute;top:calc(100% + 6px);right:0;min-width:160px;padding:6px;border-radius:10px;border:1px solid var(--policy-claim-menu-border);background-color:var(--popover);box-shadow:var(--policy-claim-menu-shadow);display:none;z-index:5;'
                      menu.setAttribute('role', 'menu')

                      const makeMenuItem = (label: string, onClick: () => void) => {
                        const item = document.createElement('button')
                        item.type = 'button'
                        item.textContent = label
                        item.setAttribute('role', 'menuitem')
                        item.style.cssText = 'display:block;width:100%;text-align:left;padding:6px 10px;border:none;background:none;border-radius:6px;font-size:12px;font-family:Inter,sans-serif;color:var(--policy-claim-action-text);cursor:pointer;transition:background-color 0.1s ease;'
                        item.onmouseenter = () => { item.style.backgroundColor = 'var(--policy-claim-citation-menu-hover-bg)' }
                        item.onmouseleave = () => { item.style.backgroundColor = 'transparent' }
                        item.onclick = (event) => {
                          event.preventDefault()
                          event.stopPropagation()
                          menu.style.display = 'none'
                          onClick()
                        }
                        menu.appendChild(item)
                      }

                      makeMenuItem('View claim in sidebar', () => {
                        plainMarker.dispatchEvent(new MouseEvent('click', { bubbles: true }))
                      })
                      makeMenuItem('Copy claim text', () => {
                        if (claim.claimText) {
                          navigator.clipboard.writeText(claim.claimText).catch(() => {})
                        }
                      })

                      let hideTimer: ReturnType<typeof setTimeout> | null = null
                      const scheduleHide = () => {
                        if (hideTimer) clearTimeout(hideTimer)
                        hideTimer = setTimeout(() => { menu.style.display = 'none' }, 120)
                      }
                      const cancelHide = () => {
                        if (hideTimer) { clearTimeout(hideTimer); hideTimer = null }
                      }

                      plainMarker.onclick = (event) => {
                        event.preventDefault()
                        event.stopPropagation()
                        onClaimClick?.(claim.id)
                        const isOpen = menu.style.display === 'block'
                        menu.style.display = isOpen ? 'none' : 'block'
                      }
                      markerWrap.onmouseleave = () => { scheduleHide() }
                      menu.onmouseenter = () => { cancelHide() }
                      menu.onmouseleave = () => { scheduleHide() }

                      markerWrap.appendChild(plainMarker)
                      markerWrap.appendChild(menu)
                    }

                    inlineWrapper.appendChild(markerWrap)
                  }

                  return inlineWrapper
                },
                { side: -1 }
              )
            )
          }

          if (showClaimMarkers && flaggedClaims.length > 0) {
            decorations.push(
              Decoration.widget(
                pos + node.nodeSize,
                () => {
                  const wrapper = document.createElement('div')
                  wrapper.style.cssText =
                    'display:flex;flex-direction:column;gap:6px;margin-top:8px;margin-bottom:4px;'
                  wrapper.setAttribute('data-policy-claim-block-id', blockId)

                  for (const [flaggedIndex, claim] of flaggedClaims.entries()) {
                    const isActive = activeClaimId === claim.id
                    const hasTemplate = Boolean(claim.templateKey)
                    const colors = getClaimColors('flagged', isActive)
                    const claimRow = document.createElement('div')
                    claimRow.style.cssText =
                      'display:flex;align-items:flex-start;gap:8px;width:100%;'
                    const badge = document.createElement('div')
                    badge.style.cssText = [
                      'display:flex',
                      'flex-direction:column',
                      'align-items:stretch',
                      'gap:8px',
                      'padding:10px 12px',
                      `background-color:${colors.bg}`,
                      `border:1px solid ${colors.border}`,
                      'border-radius:4px',
                      'cursor:default',
                      'transition:background-color 0.15s ease, border-color 0.15s ease',
                      'width:100%',
                      'box-sizing:border-box',
                    ].join('; ')
                    badge.setAttribute('data-claim-id', claim.id)

                    const text = document.createElement('div')
                    text.style.cssText = `font-size:12px;line-height:1.45;color:${colors.text};font-family:Inter,sans-serif;`
                    text.textContent = claim.claimText?.trim() || `Claim ${flaggedIndex + 1}`
                    badge.appendChild(text)

                    if (hasTemplate) {
                      const tmplBadge = document.createElement('span')
                      tmplBadge.style.cssText =
                        'width:max-content;font-size:10px;padding:4px 7px;border-radius:999px;white-space:nowrap;font-family:Inter,sans-serif;background-color:var(--policy-claim-template-bg);color:var(--policy-claim-template-text);border:1px solid var(--policy-claim-template-border);max-width:180px;overflow:hidden;text-overflow:ellipsis;'
                      tmplBadge.textContent = claim.templateName || claim.templateKey || ''
                      tmplBadge.title = claim.templateName || claim.templateKey || ''
                      badge.appendChild(tmplBadge)
                    }

                    // RFC-140 §6.3: Show match level indicator
                    if (claim.matchLevel) {
                      const matchBadge = document.createElement('span')
                      const score = claim.slotMatchScore ?? 0
                      const matchColor = score >= 0.8
                        ? {
                            bg: 'var(--policy-claim-match-success-bg)',
                            border: 'var(--policy-claim-match-success-border)',
                            text: 'var(--status-green-strong)',
                          }
                        : score >= 0.5
                          ? {
                              bg: 'var(--policy-claim-flagged-inactive-bg)',
                              border: 'var(--policy-claim-flagged-inactive-border)',
                              text: 'var(--status-orange-strong)',
                            }
                          : {
                              bg: 'var(--policy-claim-match-error-bg)',
                              border: 'var(--policy-claim-match-error-border)',
                              text: 'var(--status-red-strong)',
                            }
                      matchBadge.style.cssText = `width:max-content;font-size:10px;padding:3px 6px;border-radius:999px;white-space:nowrap;font-family:Inter,sans-serif;background-color:${matchColor.bg};color:${matchColor.text};border:1px solid ${matchColor.border};`
                      matchBadge.textContent = claim.matchLevel
                      matchBadge.title = `Match: ${claim.matchLevel} (score: ${score.toFixed(2)})`
                      badge.appendChild(matchBadge)
                    }

                    const actionRow = buildClaimActions(claim, {
                      isFlagged: true,
                      anchor: claimRow,
                    })

                    claimRow.appendChild(badge)
                    if (actionRow.childNodes.length > 0) {
                      claimRow.appendChild(actionRow)
                    }
                    wrapper.appendChild(claimRow)
                  }

                  return wrapper
                },
                { side: 1 }
              )
            )
          }
        })

        return DecorationSet.create(state.doc, decorations)
      },
    },
  })

  try {
    editor.registerPlugin(plugin)
    if (editor.view?.docView) {
      editor.view.dispatch(editor.state.tr)
    }
  } catch {
    return undefined
  }

  return () => {
    try {
      if (!editor.isDestroyed) {
        editor.unregisterPlugin(pluginKey)
      }
    } catch {
      // Ignore cleanup errors during unmount.
    }
  }
}

export function syncPolicyClaimBlockDecorations(
  container: HTMLElement,
  blockIdToClaimIds: Record<string, string[]>,
  activeBlockId?: string | null,
  claimInfoMap?: Record<string, { templateKey?: string | null; triageStatus?: string | null }>,
) {
  const touched: HTMLElement[] = []

  for (const blockId of Object.keys(blockIdToClaimIds)) {
    const blockEl = findBlockElement(container, blockId)
    if (!blockEl) continue

    const claimIds = blockIdToClaimIds[blockId]
    // A block has warnings if ANY of its claims are flagged
    const hasFlagged = claimInfoMap
      ? claimIds.some((id) => claimInfoMap[id]?.triageStatus === 'flagged')
      : false

    blockEl.dataset.policyClaimBlockId = blockId
    const isActive = blockId === activeBlockId
    if (hasFlagged) {
      const palette = CLAIM_BADGE_COLORS.flagged
      blockEl.style.backgroundColor = isActive ? palette.block.active : palette.block.inactive
      blockEl.style.boxShadow = isActive ? palette.blockShadow.active : palette.blockShadow.inactive
      blockEl.style.borderRadius = '6px'
    } else if (isActive) {
      // Highlight active block when a claim is selected
      blockEl.style.backgroundColor = 'var(--policy-claim-selected-bg)'
      blockEl.style.boxShadow = 'var(--policy-claim-selected-shadow)'
      blockEl.style.borderRadius = '6px'
    } else {
      // No background for inactive default claims
      blockEl.style.backgroundColor = ''
      blockEl.style.boxShadow = ''
      blockEl.style.borderRadius = ''
    }
    blockEl.style.transition = 'background-color 0.2s ease, box-shadow 0.2s ease'
    touched.push(blockEl)
  }

  return () => {
    for (const blockEl of touched) {
      delete blockEl.dataset.policyClaimBlockId
      blockEl.style.backgroundColor = ''
      blockEl.style.boxShadow = ''
      blockEl.style.borderRadius = ''
      blockEl.style.transition = ''
    }
  }
}

export function syncPolicyClaimBlockTargets(
  container: HTMLElement,
  blockIdToClaimIds: Record<string, string[]>,
) {
  const touched: HTMLElement[] = []

  for (const blockId of Object.keys(blockIdToClaimIds)) {
    const blockEl = findBlockElement(container, blockId)
    if (!blockEl) continue

    blockEl.dataset.policyClaimBlockId = blockId
    touched.push(blockEl)
  }

  return () => {
    for (const blockEl of touched) {
      delete blockEl.dataset.policyClaimBlockId
    }
  }
}

// Track the currently highlighted block so we can clear it on next click
let currentHighlightedBlock: HTMLElement | null = null

export function scrollToPolicyClaimBlock(
  container: HTMLElement | null,
  claimIdToBlockId: Record<string, string>,
  claimId: string,
) {
  // Clear previous highlight
  if (currentHighlightedBlock) {
    currentHighlightedBlock.classList.remove('policy-claim-block-active')
    currentHighlightedBlock = null
  }

  if (!container) return false

  const blockId = claimIdToBlockId[claimId]
  if (!blockId) return false

  const blockEl = findBlockElement(container, blockId)
  if (!blockEl) return false

  // Apply highlight to the visible block element. For list items, the
  // data-id is on the <p> inside <li> — highlight the <li> instead.
  const highlightTarget = blockEl.closest('li') ?? blockEl
  highlightTarget.classList.add('policy-claim-block-active')
  currentHighlightedBlock = highlightTarget

  const scrollContainer = findScrollableAncestorWithin(container, blockEl)

  if (!scrollContainer) {
    blockEl.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest',
    })
    return true
  }

  const containerRect = scrollContainer.getBoundingClientRect()
  const blockRect = blockEl.getBoundingClientRect()
  const blockTop =
    blockRect.top - containerRect.top + scrollContainer.scrollTop
  const targetScrollTop = Math.max(
    0,
    blockTop - (scrollContainer.clientHeight - blockRect.height) / 2
  )

  scrollContainer.scrollTo({
    top: targetScrollTop,
    behavior: 'smooth',
  })

  return true
}
